import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  FormControl,
  TextField,
  Autocomplete,
  IconButton,
  ListItem,
  ListItemText,
  Button,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import { textFieldStyles } from '../styles/textFieldStyles';
import TimesheetDropdownItem from './TimesheetDropdownItem';

/**
 * TimesheetSelector component - Combines the timesheet clone dropdown and add button
 * into a single integrated module
 */
const TimesheetSelector = ({
  timesheets = [],
  selectedTimesheet,
  onTimesheetSelect,
  onAddTimesheet,
  width = '100%'
}) => {
  const { t } = useTranslation(['forms', 'common']);
  // State to track which buttons show the tick icon
  const [tickButtons, setTickButtons] = useState({});

  // Group timesheets by unique dates
  const uniqueDates = useMemo(() => {
    const dates = {};
    timesheets.forEach(timesheet => {
      if (timesheet?.date) {
        try {
          const dateStr = new Date(timesheet.date).toISOString().split('T')[0];
          if (!dates[dateStr]) {
            dates[dateStr] = {
              date: timesheet.date,
              formattedDate: new Date(timesheet.date).toLocaleDateString(),
              timesheets: []
            };
          }
          dates[dateStr].timesheets.push(timesheet);
        } catch (e) {
          console.error('Invalid date format:', timesheet.date);
        }
      }
    });
    return Object.values(dates);
  }, [timesheets]);

  // Format the label to show just the date with timesheet count
  const getFormattedLabel = (option) => {
    if (!option) return '';
    return `${option.formattedDate} (${option.timesheets.length})`;
  };

  // Handle creating a new timesheet when "+" is clicked on a date option
  const handleCreateFromTemplate = (date, timesheets) => {
    // Use the first timesheet from this date as the template
    onAddTimesheet(timesheets[0]);
  };

  return (
    <Box sx={{ width }}>
      <FormControl fullWidth>
        <Autocomplete
          options={uniqueDates}
          getOptionLabel={getFormattedLabel}
          value={selectedTimesheet ? uniqueDates.find(d => 
            d.timesheets.some(t => t === selectedTimesheet)) : null}
          onChange={(event, newValue) => {
            // If a date is selected, pass the first timesheet from that date
            if (newValue && newValue.timesheets.length > 0) {
              onTimesheetSelect(event, newValue.timesheets[0]);
            } else {
              onTimesheetSelect(event, null);
            }
          }}
          renderOption={(props, option) => (
            <TimesheetDropdownItem
              {...props}
              date={option.date}
              count={option.timesheets.length}
              onAdd={() => handleCreateFromTemplate(option.date, option.timesheets)}
            />
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('common:select_timesheet')}
              placeholder={t('common:select_timesheet')}
              size="small"
              sx={textFieldStyles()}
            />
          )}
          ListboxProps={{
            sx: { 
              maxHeight: '250px',
              padding: 0,
              '& .MuiAutocomplete-option': {
                padding: '0',
                minHeight: '32px',
                overflow: 'visible'
              },
              overflowY: 'auto',
              overflowX: 'hidden'
            }
          }}
          PopperProps={{
            style: { 
              width: 'auto', 
              minWidth: '240px',
              maxWidth: '300px',
              overflow: 'hidden'
            },
            disablePortal: true
          }}
          slotProps={{
            paper: {
              style: {
                overflow: 'hidden'
              }
            }
          }}
        />
      </FormControl>
    </Box>
  );
};

export default TimesheetSelector; 